import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import {
  ArrowLeft,
  AudioLines,
  Clock,
  LoaderCircle,
  Music,
  Pause,
  Play,
  Trash2,
} from 'lucide-react'
import { motion } from 'motion/react'
import { useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  type AudioGenerationListItem,
  type AudioGenerationStatus,
  useAudioGenerationsQuery,
  useDeleteAudioGenerationMutation,
} from '@/features/audio/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/audio-library')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' })
    }
  },
  component: AudioLibraryPage,
})

type FilterTab = 'all' | AudioGenerationStatus

const FILTER_TABS: { value: FilterTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'processing', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
]

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const },
  },
}

const STATUS_CONFIG: Record<
  AudioGenerationStatus,
  { label: string; className: string }
> = {
  processing: {
    label: 'Processing',
    className: 'bg-blue-100 text-blue-700 animate-pulse',
  },
  completed: { label: 'Ready', className: 'bg-emerald-100 text-emerald-800' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-800' },
}

const WAVEFORM_BARS = [
  40, 65, 50, 80, 45, 70, 55, 85, 60, 40, 75, 50, 65, 45, 70,
].map((h, i) => ({ key: `bar-${String(i)}`, h }))

function formatRelative(isoDate: string): string {
  const date = new Date(isoDate)
  const diffMs = Date.now() - date.getTime()
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return date.toLocaleDateString()
}

function FilterBar({
  active,
  onSelect,
}: {
  active: FilterTab
  onSelect: (tab: FilterTab) => void
}) {
  return (
    <div className="flex gap-1 rounded-lg bg-muted p-1">
      {FILTER_TABS.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onSelect(tab.value)}
          className={cn(
            'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
            active === tab.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

function WaveformPlaceholder() {
  return (
    <div className="flex items-end justify-center gap-[3px]" aria-hidden="true">
      {WAVEFORM_BARS.map((bar) => (
        <div
          key={bar.key}
          className="w-1 rounded-full bg-zinc-600"
          style={{ height: `${bar.h}%` }}
        />
      ))}
    </div>
  )
}

function StatusPill({ status }: { status: AudioGenerationStatus }) {
  const config = STATUS_CONFIG[status]
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        config.className,
      )}
    >
      {config.label}
    </span>
  )
}

function AudioCard({
  item,
  onDelete,
  isDeleting,
}: {
  item: AudioGenerationListItem
  onDelete: (id: string) => void
  isDeleting: boolean
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  function togglePlay() {
    if (!item.audio_url) return
    if (!audioRef.current) {
      audioRef.current = new Audio(item.audio_url)
      audioRef.current.addEventListener('ended', () => setIsPlaying(false))
    }
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      void audioRef.current.play()
      setIsPlaying(true)
    }
  }

  const title = item.caption || 'Untitled track'

  return (
    <div className="group overflow-hidden rounded-xl border bg-card shadow-card transition-shadow duration-200 hover:shadow-card-hover">
      <div className="relative flex aspect-[2.5/1] items-center justify-center bg-zinc-900 px-6">
        <WaveformPlaceholder />
        <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm">
          <Music className="size-3" />
          Song
        </span>
        <span className="absolute top-2.5 right-2.5">
          <StatusPill status={item.status} />
        </span>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <h3 className="line-clamp-1 text-sm font-semibold leading-snug">
          {title}
        </h3>

        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="size-3" />
            {formatRelative(item.created_at)}
          </span>

          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={!item.audio_url}
              onClick={togglePlay}
              aria-label={isPlaying ? `Pause ${title}` : `Play ${title}`}
            >
              {isPlaying ? (
                <Pause className="size-3.5" />
              ) : (
                <Play className="size-3.5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-destructive"
              disabled={isDeleting}
              onClick={() => {
                if (confirm(`Delete "${title}"?`)) onDelete(item.id)
              }}
              aria-label={`Delete ${title}`}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <div className="flex size-16 items-center justify-center rounded-2xl bg-muted">
        <AudioLines
          className="size-8 text-muted-foreground"
          strokeWidth={1.4}
        />
      </div>
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">No audio yet</h2>
        <p className="text-sm text-muted-foreground">
          Generate a song or clone a voice to see it here.
        </p>
      </div>
      <Button asChild>
        <Link to="/create">Create a video</Link>
      </Button>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-muted-foreground">
      <LoaderCircle className="size-6 animate-spin" />
      <p className="text-sm">Loading your audio...</p>
    </div>
  )
}

function AudioLibraryPage() {
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all')

  const { data, isLoading } = useAudioGenerationsQuery(
    activeFilter === 'all' ? undefined : { status: activeFilter },
  )
  const deleteMutation = useDeleteAudioGenerationMutation()

  return (
    <div className="relative min-h-screen px-4 pb-16 pt-6 sm:px-6 lg:px-8">
      <motion.header
        className="mx-auto mb-8 flex max-w-5xl flex-wrap items-center gap-3"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <Button variant="ghost" size="icon" className="shrink-0" asChild>
          <Link to="/dashboard" aria-label="Back to dashboard">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Audio Library</h1>
        {data && (
          <span className="text-sm text-muted-foreground">({data.total})</span>
        )}
        <div className="ml-auto">
          <FilterBar active={activeFilter} onSelect={setActiveFilter} />
        </div>
      </motion.header>

      <div className="mx-auto max-w-5xl">
        {isLoading ? (
          <LoadingState />
        ) : !data || data.items.length === 0 ? (
          <EmptyState />
        ) : (
          <motion.div
            key={activeFilter}
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
            variants={stagger}
            initial="hidden"
            animate="visible"
          >
            {data.items.map((item) => (
              <motion.div key={item.id} variants={fadeUp}>
                <AudioCard
                  item={item}
                  onDelete={(id) => deleteMutation.mutate(id)}
                  isDeleting={
                    deleteMutation.isPending &&
                    deleteMutation.variables === item.id
                  }
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  )
}
